import React, { useEffect } from "react";

import { inputStateForValue, useTranslation } from "utils";

import { HARD_INPUT_STATES, HARVEST_UNIT_VALUE, HarvestUnitValue, INPUT_STATE, toTonnesPerHa } from "consts";

import { Flex, Form, InputNumberWithUnitSelectionProps, InputNumberWithUnitSelector, KlimRadioGroup, SanityCheck, TextArea, Typography } from "components";

import { CROP } from "pages/app/all-field-view/constants/cropForm.ts";
import { YieldFormValidationError, YieldFormValidationWarning } from "pages/app/all-field-view/utils/AllFieldsUtils.ts";

export type CropYieldSectionProps = {
  label: React.ReactNode;
  labelClassName?: string;
  initialValue: number | null;
  initialValueUnit: HarvestUnitValue | null;
  initialIsHarvested: boolean | null;
  disabled: boolean;
  isRequired: boolean | undefined;
  isZeroYieldsAllowed: boolean;
  help?: React.ReactNode;
  onChange: InputNumberWithUnitSelectionProps["onChange"];
  committedYield: number | null;
  committedYieldUnit: HarvestUnitValue | null;
  yieldBenchmarkBoundsTperHa?: PracticeManagement.ParameterBounds;
  cropName?: string | null;
  initialNoteYield?: string | null;
};

export const CropYieldSection = ({
  label,
  labelClassName,
  initialValue,
  initialValueUnit,
  initialIsHarvested,
  disabled,
  isRequired,
  isZeroYieldsAllowed,
  help,
  onChange,
  committedYield,
  committedYieldUnit,
  yieldBenchmarkBoundsTperHa,
  initialNoteYield,
}: CropYieldSectionProps) => {
  const { t } = useTranslation("allFieldView");
  const form = Form.useFormInstance();
  const wasHarvested = Form.useWatch([CROP, "wasHarvested"], form);

  useEffect(() => {
    if (wasHarvested === false && isZeroYieldsAllowed) {
      form.setFieldValue([CROP, "yieldPerHa"], null);
      form.setFieldValue([CROP, "yieldPerHaUnit"], null);
      form.setFieldValue([CROP, "sanityNoteYield"], undefined);
    }
  }, [wasHarvested]); // eslint-disable-line react-hooks/exhaustive-deps

  // Because of the gross nesting of `Form.Item` inside of the `Component` lib
  // the correct field values don't always bubble upwards for validation, so you
  // have to register the value in the form on load directly
  useEffect(() => {
    if (form.getFieldValue([CROP, "yieldPerHaUnit"]) === undefined) {
      form.setFieldValue([CROP, "yieldPerHaUnit"], initialValueUnit ?? null);
    }
  }, [initialValueUnit]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleYieldChange: CropYieldSectionProps["onChange"] = (value, unit) => {
    onChange?.(value, unit);
    form.validateFields([
      [CROP, "yieldPerHa"],
      [CROP, "sanityNoteYield"],
    ]);
  };

  const yieldInTs = toTonnesPerHa(committedYield, committedYieldUnit);
  const yieldInputState =
    committedYield || (committedYield === 0 && isZeroYieldsAllowed)
      ? yieldInTs
        ? inputStateForValue(yieldInTs, yieldBenchmarkBoundsTperHa)
        : INPUT_STATE.expected
      : INPUT_STATE.expected;
  const isYieldOutsideExpectedRange = yieldInputState !== INPUT_STATE.expected;
  const isYieldImplausible = HARD_INPUT_STATES.includes(yieldInputState);

  return (
    <Flex className="add-edit-crop-form">
      <div className="form-container" data-testid="add-edit-crop-form-yield-widget">
        <Typography variant="h5" className="margin-bottom-100">
          {t("cropForm.yieldSectionTitle", { defaultValue: "Yield" })}
        </Typography>
        {isZeroYieldsAllowed && (
          <Flex justify="space-between" gap="1rem" className="margin-top-100 margin-bottom-100">
            <Typography variant="paragraph-m">{t("cropForm.wasHarvested.question", { defaultValue: "Was this crop harvested?" })}*</Typography>
            <Form.Item
              name={[CROP, "wasHarvested"]}
              initialValue={initialIsHarvested ?? !isZeroYieldsAllowed}
              rules={[
                { required: true, message: t("cropForm.wasHarvested.errorMessage", { defaultValue: "Please indicate whether this crop was harvested" }) },
              ]}
            >
              <KlimRadioGroup
                buttonStyle="solid"
                className="radio-bg-white"
                dataTestId="was-harvested-toggle"
                fullWidth
                options={[
                  { value: true, label: t("cropForm.wasHarvested.yes", { defaultValue: "Yes" }) },
                  { value: false, label: t("cropForm.wasHarvested.no", { defaultValue: "No" }) },
                ]}
                optionType="button"
                size="large"
                type="tertiary"
              />
            </Form.Item>
          </Flex>
        )}
        {/* For non-harvestable crops the question is shown and gates the yield input; for harvestable crops yield is always shown */}
        {(!isZeroYieldsAllowed || wasHarvested !== false) && (
          <Flex vertical gap={16}>
            <InputNumberWithUnitSelector
              dataTestId="add-edit-crop-form-yield"
              name={[CROP, "yieldPerHa"]}
              // Sanity check below does this for us
              className={isYieldImplausible ? "hide-error-message" : ""}
              validateStatus={isYieldImplausible ? "error" : undefined}
              label={label}
              rules={[
                {
                  validator: (_, value: number | null) =>
                    disabled ? Promise.resolve() : YieldFormValidationError({ value, valueUnit: committedYieldUnit, isRequired, isZeroYieldsAllowed, t }),
                },
                {
                  warningOnly: true,
                  validator: (_, value: number | null) =>
                    disabled ? Promise.resolve() : YieldFormValidationWarning({ value, isRequired, isZeroYieldsAllowed, initialValue, t }),
                },
              ]}
              initialValue={initialValue}
              value={committedYield}
              unitValue={committedYieldUnit ?? initialValueUnit ?? null}
              step={0.01}
              units={[HARVEST_UNIT_VALUE.tonnesPerHa, HARVEST_UNIT_VALUE.decitonnesPerHa]}
              unitLabel={t("unit", { ns: "footprinting" })}
              labelClassName={labelClassName}
              disabled={disabled}
              help={help}
              onChange={handleYieldChange}
            />
            {isYieldOutsideExpectedRange && (
              <>
                <SanityCheck
                  inputValue={committedYield ?? 0}
                  inputRange={
                    committedYieldUnit === HARVEST_UNIT_VALUE.decitonnesPerHa
                      ? (yieldBenchmarkBoundsTperHa?.map((v) => v * 10) as PracticeManagement.ParameterBounds)
                      : yieldBenchmarkBoundsTperHa
                  }
                  unit={committedYieldUnit}
                />
                <TextArea
                  dataTestId="add-edit-crop-form-yield-sanity-note"
                  name={[CROP, "sanityNoteYield"]}
                  initialValue={initialNoteYield}
                  label={t("cropForm.yieldSanityCheck.explinationRequired")}
                  rules={[
                    {
                      required: true,
                      message: t("cropForm.yieldSanityCheck.explinationRequired"),
                    },
                  ]}
                  autoSize
                  help={help}
                  disabled={disabled}
                />
              </>
            )}
          </Flex>
        )}
      </div>
    </Flex>
  );
};
